import { useRef, useState } from 'react';

import type { BlockResponse } from '../api';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { usePdfRenderer } from '../hooks/usePdfRenderer';
import { BlockLegend } from './BlockLegend';
import { BlocksOverlay } from './BlocksOverlay';
import { DocumentNavigation } from './DocumentNavigation';
import { formatPageRange } from './pageRange';

interface PdfViewerProps {
  url: string;
  /** First page (1-based, inclusive) the viewer may show. Both bounds required to take effect. */
  startPage?: number | null;
  /** Last page (1-based, inclusive) the viewer may show. */
  endPage?: number | null;
  /** Mistral OCR 4 structural blocks to draw as a toggleable overlay; null/empty hides the control. */
  blocks?: ReadonlyArray<BlockResponse> | null;
}

/** Caption giving window context, e.g. «Páginas 6–8 del archivo original». */
function windowCaption(firstPage: number, lastPage: number): string {
  return `${formatPageRange(firstPage, lastPage)} del archivo original`;
}

/**
 * Renders a PDF inside a canvas with sticky page navigation. No overlay layer — the reviewer
 * surfaces extracted fields exclusively through the side panel.
 *
 * When `startPage`/`endPage` are set (segment of a split upload), navigation is bounded to that
 * range while page numbers stay absolute to the parent PDF — the blob is never sliced.
 *
 * Layout: the root is a height-bounded flex column. The canvas wrap takes the remaining vertical
 * space and scrolls independently when the rendered page is taller than its container. The
 * navigation bar is sticky to the bottom of the column.
 *
 * Performance: `pdfjs-dist` is dynamically imported by `usePdfRenderer`, so the worker only
 * loads when the user actually navigates here.
 */
export function PdfViewer({ url, startPage, endPage, blocks }: PdfViewerProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showStructure, setShowStructure] = useState(true);

  const hasWindow = typeof startPage === 'number' && typeof endPage === 'number';
  const { isLoading, pageNumber, firstPage, lastPage, viewport, error, nextPage, prevPage } = usePdfRenderer(
    url,
    canvasRef,
    containerRef,
    hasWindow ? { startPage, endPage } : undefined,
  );

  const hasBlocks = Array.isArray(blocks) && blocks.length > 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {hasBlocks ? (
        <button
          type="button"
          onClick={() => setShowStructure((value) => !value)}
          aria-pressed={showStructure}
          className="absolute right-3 top-3 z-10 rounded-md border border-slate-300 bg-white/90 px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm hover:bg-white"
        >
          {showStructure ? 'Ocultar estructura' : 'Estructura OCR'}
        </button>
      ) : null}
      {hasBlocks && showStructure ? (
        <div className="absolute left-3 top-3 z-10 max-w-[60%]">
          <BlockLegend blocks={blocks!} currentPage={pageNumber} />
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="relative min-h-0 flex-1 overflow-auto bg-slate-50"
        aria-busy={isLoading}
      >
        {error !== null ? (
          <div className="mx-auto mt-12 max-w-md rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <p className="font-semibold">No se pudo cargar el documento.</p>
            <p className="mt-1">{error}</p>
          </div>
        ) : (
          <div className="relative mx-auto inline-block py-6">
            <div className="relative">
              <canvas
                ref={canvasRef}
                aria-label="Vista previa del documento"
                className="block shadow-md ring-1 ring-slate-200/70"
              />
              {hasBlocks && showStructure && viewport !== null ? (
                <BlocksOverlay blocks={blocks!} currentPage={pageNumber} viewport={viewport} />
              ) : null}
            </div>
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-50/60">
                <LoadingSpinner size="md" label="Cargando PDF…" />
              </div>
            ) : null}
          </div>
        )}
      </div>
      <DocumentNavigation
        pageNumber={pageNumber}
        firstPage={firstPage}
        lastPage={lastPage}
        {...(hasWindow && lastPage > 0
          ? { windowCaption: windowCaption(firstPage, lastPage) }
          : {})}
        onPrev={prevPage}
        onNext={nextPage}
      />
    </div>
  );
}
